import React, { useState, useEffect } from "react";
import { css } from '@emotion/css';
import { color_colors_growth, color_colors_ocean, color_shades_dark, color_text_default, color_text_light, pastel_green, pastel_red, silver_four, color_shades_darker } from "../../../constants/colors";
import ToggleIndicator from "../../../components/general/ToggleIndicator";
import Button from "../../../components/general/Button";
import HorizontalSeparator from "../../../components/structure/HorizontalSeparator";
import UserApplicationPreferencesContainer from "../../../state/containers/UserApplicationPreferencesContainer";
import { SimulatorMessage } from "./SimulatorInteraction";
import CustomScrollbars from "../../../components/structure/CustomScrollbars";
import useWindowSize from "../../../hooks/UseWindowSize";
import styled from "@emotion/styled";
import TestCaseContainer from "../../../hooks/TestCaseContainer";
import TestCase from "../../../models/customAssistant/TestCase";
import TestCaseEditingModal from "../../../components/forms/EditTestCase";
import SecondaryLoader from "../../../components/general/SecondaryLoader";
import set from 'lodash';
import SearchField from "../../../components/forms/SearchField";
import CheckboxField from "../../../components/forms/CheckboxField";
import TooltipWrapper from "../../../components/general/TooltipWrapper";
import DeleteConfirmation from "../../featureContent/components/DeleteConfirmation";
import ConfirmationDialog from "../../../components/structure/ConfirmationDialog";
import ApplicationModel from "../../../models/applications/api/ApplicationModel";
import { LabeledText } from "../../applicationSettings/components/SharedStyledComponents";

const removeIcon = require("../../../content/images/ssml/remove-circle-dark.svg");
const copyIcon = require("../../../content/images/content-explorer/copy.svg");
const arrowRight = require("../../../content/images/arrow-circle-right-dark.svg");
const refresh = require("../../../content/images/refresh-dark.svg");
const greenPowerIcon = require("../../../content/images/simulator/green-power.svg");

interface ContentItemTestCasesPanelProps {
    application: ApplicationModel,
    contentItemId?: string,
    runTestCase: (testCase: TestCase) => void,
    simulateTestCase: (testCase: TestCase) => void,
    responseIdToNamesRef: { current: Record<string, string> }
};

const ContentItemTestCasesPanel: React.FC<ContentItemTestCasesPanelProps> = (props) => {
    const windowSize = useWindowSize();
    const testCaseContainer = TestCaseContainer.useContainer();
    const [testCase, setTestCase] = useState<TestCase>(null);
    const [checkedCases, setCheckedCases] = useState<string[]>([]);
    const [search, setSearch] = useState<string>("");
    const [nameToDelete, setNameToDelete] = useState<string>("");
    const [responseNamesForDisplay, setResponseNamesForDisplay] = useState({})

    useEffect(() => {
        testCaseContainer.setApplication(props.application)
        if (props.contentItemId?.length > 0) {
            testCaseContainer.loadAppLevelTestCases(props.application.id, props.contentItemId)
        }
        else {
            testCaseContainer.loadAppLevelTestCases(props.application.id);
        }
        var updatedResponseNamesList = {}
        setResponseNamesForDisplay(updatedResponseNamesList);
    }, []);


    const confirmDelete = () => {
        if (nameToDelete) {
            testCaseContainer.deleteAppLevelTestCase(props.application.id, nameToDelete, props.contentItemId).then(() => {
                setNameToDelete("");
                testCaseContainer.loadAppLevelTestCases(props.application.id);
            });
        }
    }

    const closeDeleteConfirmation = () => {
        setNameToDelete("");
    }

    const runTestCase = async (name: string, contentItemId?: string) => {
        const testCase = await testCaseContainer.loadAppLevelTestCase(props.application.id, name, contentItemId);
        if (testCase) {
            props.runTestCase(testCase);
        }
    }

    const simulateTestCase = async (name: string, contentItemId?: string) => {
        const testCase = await testCaseContainer.loadAppLevelTestCase(props.application.id, name, contentItemId);
        if (testCase) {
            props.simulateTestCase(testCase);
        }
    }

    const editTestCase = async (name: string, contentItemId?: string) => {
        const testCase = await testCaseContainer.loadAppLevelTestCase(props.application.id, name, contentItemId);
        if (testCase) {
            setTestCase(testCase);
        }
    }

    const getTestCaseColor = (name: string) => {
        const testCaseResults = testCaseContainer.testCaseResults;
        if (testCaseResults && testCaseResults[name]) {
            if (Object.keys(testCaseResults[name]).some(k => testCaseResults[name][k]?.toLowerCase() !== "true"))
                return "red";
            return "green";
        }
        else
            return "";
    }

    const isLoadingCase = (name: string) => {
        if (testCaseContainer.runningTests?.length && testCaseContainer.runningTests.includes(name)) {
            const testCaseResults = testCaseContainer.testCaseResults;
            if (testCaseResults && testCaseResults[name]) {
                return false;
            }
            return true;
        } else {
            return false;
        }
    }

    const handleSearch = (searchEvent: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(searchEvent.target.value);
    };

    const contentItemTestCases = testCaseContainer.availableTestCases?.contentItemTestCases ?? {};
    let testCases = (props.contentItemId in contentItemTestCases) ? contentItemTestCases[props.contentItemId] : []

    if (search?.length) {
        testCases = [];
        const searchableTestCases = (props.contentItemId in contentItemTestCases) ? contentItemTestCases[props.contentItemId] : []
        for (const c of searchableTestCases) {
            if (c.toLowerCase().includes(search.toLowerCase())) {
                testCases.push(c);
            }
        }
    }
    // alphabetically sort test casess
    testCases = testCases.sort((a, b) => a.localeCompare(b));
    return (
        <div className={containerStyle}>
            <TestCaseHeadingWrapper>
                <div className="title-container">
                    <div className="title">Test Cases</div>
                </div>
                <StyledTopButton themes={["white-pill"]} text="Run All" onClick={async () => {
                    const responses = await testCaseContainer.runAppLevelTestCases()
                    var updatedResponseNamesList = {};
                    for (const tcname in responses) {
                        updatedResponseNamesList[tcname] = props.responseIdToNamesRef.current[responses[tcname]?.responseId];
                    }
                    setResponseNamesForDisplay(updatedResponseNamesList);
                }} />
                {checkedCases?.length ?
                    <StyledTopButton themes={["white-pill"]} text="Run Checked" onClick={async () => {
                        const responses = await testCaseContainer.runAppLevelTestCases(checkedCases)
                        var updatedResponseNamesList = {};
                        for (const tcname in responses) {
                            const updatedResponse = (checkedCases.includes(tcname)) ? props.responseIdToNamesRef.current[responses[tcname]?.responseId] : "";
                            updatedResponseNamesList[tcname] = updatedResponse
                        }
                        setResponseNamesForDisplay(updatedResponseNamesList);
                    }} />
                    : <></>}
                <SearchRow>
                    <SearchCheckbox
                        key={`check.${name}`}
                        checked={testCases?.length && testCases.every(tc => checkedCases.includes(tc))}
                        onClick={(e) => {
                            if (testCases?.length) {
                                if (testCases.every(tc => checkedCases.includes(tc))) {
                                    setCheckedCases([]);
                                } else {
                                    setCheckedCases([...testCases]);
                                }
                            }
                        }}
                        label={""}
                    />
                    <CustomSearch
                        id="1"
                        name={"searchField"}
                        placeholder={"Search for a test case"}
                        disabled={false}
                        onChange={handleSearch} />
                </SearchRow>
            </TestCaseHeadingWrapper>
            <CustomScrollbars autoHide autoHeight autoHeightMax={(windowSize.windowSize.innerHeight * .9) - 175}>
                <Phrases>
                    {testCases.map((name, idx) =>
                        <div>
                            <Phrase key={idx}>
                                <StyledCheckbox
                                    key={`check.${name}`}
                                    checked={checkedCases.includes(name)}
                                    onClick={(e) => {
                                        if (checkedCases.includes(name)) {
                                            setCheckedCases(checkedCases.filter(c => c !== name));
                                        } else {
                                            setCheckedCases([...checkedCases, name]);
                                        }
                                    }}
                                    label={""}
                                />
                                {isLoadingCase(name) ?
                                    <TestCaseChip className={getTestCaseColor(name)} onClick={() => editTestCase(name, props.contentItemId)}>
                                        <SecondaryLoader />
                                    </TestCaseChip>
                                    :
                                    <TestCaseChip className={getTestCaseColor(name)} onClick={() => editTestCase(name, props.contentItemId)}>
                                        {/* truncate name */}
                                        {name.length > 30 ? name.substring(0, 30) + "..." : name}
                                    </TestCaseChip>
                                }
                                <TooltipWrapper place="top" text="Delete">
                                    <div className="remove-thumbtack" onClick={() => {
                                        setNameToDelete(name);
                                    }}>
                                        <img src={removeIcon} />
                                    </div>
                                </TooltipWrapper>
                                <TooltipWrapper place="top" text="Copy">
                                    <div className="remove-thumbtack" onClick={async () => {
                                        const testCase = await testCaseContainer.loadAppLevelTestCase(props.application.id, name, props.contentItemId);
                                        if (testCase) {
                                            testCase.name = testCase.name + "-copy";
                                            testCaseContainer.createTestCase(props.application.id, testCase);
                                            const newAvailableTestCases = {
                                                ...testCaseContainer.availableTestCases,
                                                contentItemTestCases: {
                                                    ...testCaseContainer.availableTestCases?.contentItemTestCases,
                                                    [props.contentItemId]: [...testCaseContainer.availableTestCases.contentItemTestCases[props.contentItemId], testCase.name]
                                                }
                                            }
                                            testCaseContainer.setAvailableTestCases(newAvailableTestCases);
                                        }
                                    }} >
                                        <img src={copyIcon} />
                                    </div>
                                </TooltipWrapper>
                                <TooltipWrapper place="top" text="Run">
                                    <div className="remove-thumbtack" onClick={async () => {
                                        await runTestCase(name, props?.contentItemId);
                                    }}>
                                        <img src={refresh} />
                                    </div>
                                </TooltipWrapper>
                                <TooltipWrapper place="top" text="Simulate response">
                                    <div className="remove-thumbtack" onClick={async () => {
                                        await simulateTestCase(name, props?.contentItemId);
                                    }}>
                                        <img src={arrowRight} />
                                    </div>
                                </TooltipWrapper>
                            </Phrase>
                            <Phrase>
                                <TestCaseLabel>{responseNamesForDisplay[name]}</TestCaseLabel>
                            </Phrase>
                        </div>
                    )}
                </Phrases>

            </CustomScrollbars>
            {testCase != null ? <TestCaseEditingModal
                initialValue={testCase}
                onSave={async (tc) => {
                    await testCaseContainer.deleteAppLevelTestCase(props.application.id, testCase.name);
                    await testCaseContainer.createTestCase(props.application.id, tc);
                    testCaseContainer.loadAppLevelTestCases(props.application.id);
                    setTestCase(null);
                }} onClose={() => setTestCase(null)}
                isContentItemView={true} /> : <></>}

            {nameToDelete?.length ?
                <ConfirmationDialog title="Delete" deleteText="Yes, Delete"
                    isLoading={testCaseContainer.loading}
                    onClose={() => closeDeleteConfirmation()}
                    onConfirm={() => confirmDelete()}
                >
                    <DeleteConfirmation
                        itemLabel={` this test case? ${nameToDelete}`} />
                </ConfirmationDialog>
                :
                <></>
            }
        </div>
    )
}

const SearchRow = styled.div`
    display: flex;
    flex-direction: row;`;
const TestCaseHeadingWrapper = styled.div`
    padding: 0 20px 0px 24px;
`;

const Phrases = styled.div`
    padding: 0 24px;
    padding-bottom: 40px;
    width: 100%;
`;

const Phrase = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    margin: 8px 0;
    cursor: pointer;
    .remove-thumbtack {
        width: 24px;
        margin-left: 8px;
        img {
            width: 100%;
            margin-top: 10px;
        }
    }
    &:first-of-type {
        margin-top: 16px;
    }
    &:last-of-type {
        margin-bottom: 16px;
    }
`;

const CustomSearch = styled(SearchField)`    
    margin-bottom: 0px;
    div > .field-container {
        height: 32px;
        box-shadow: none;
        border-radius: 8px;
    }
`;

const StyledTopButton = styled(Button)`
    margin-top: 0px;
    margin-bottom: 8px;
    margin-right: 8px;
    margin-left: 0px;
`;

const TestCaseChip = styled.div`
    &.red {
        background: ${pastel_red};
    }
    &.green {
        background: ${pastel_green};
    }
    overflow: hidden;
    background: white;
    border: 1px solid ${silver_four};
    border-radius: 8px;
    width: 100%;
    height: 32px;
    padding: 6px 16px;    
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    .spinner > div {
        background-color: ${color_colors_ocean};
    }
    .spinner {
        height: 24px;
        margin-top: -3px;
    }
`;

const TestCaseLabel = styled.label`
    font-size: 14px;
    line-height: 20px;
    font-weight: 300;
    color: ${color_shades_darker}; // or any light gray you’re using
    padding-left: 4px;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: inline-block;
    margin-left: 32px;
`;

const StyledCheckbox = styled(CheckboxField)`
    margin-top: 4px;
`
const SearchCheckbox = styled(CheckboxField)`
    margin-top: 12px;
`
const containerStyle = css`
    padding-top: 32px;
    max-width: 432px;
    font-family: Muli;
    font-style: normal;
    font-weight: normal;
    .title-container {
        .title {
            font-size: 18px;
            line-height: 24px;
            padding-bottom: 18px;
            color: ${color_text_default}
        }
        .subtitle {
            font-size: 14px;
            line-height: 20px;
            padding-bottom: 12px;
            color: ${color_text_light}
        }
    }
`;

const RestartButton = styled(StyledTopButton)`
    color: ${color_colors_growth};
`;

export default ContentItemTestCasesPanel